import { useThree } from '@react-three/fiber';
import {
    memo,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
} from 'react';
import {
    Color,
    FrontSide,
    PlaneGeometry,
    Vector2,
} from 'three';
import type { ShaderMaterial } from 'three';
import { Reflector } from 'three/examples/jsm/objects/Reflector.js';
import { SHADOW_BAKE_ACTIVE_USER_DATA_KEY } from '../../Shadows/config';
import {
    STUDIO_REFLECTION_CAPTURE_ACTIVE_USER_DATA_KEY,
    STUDIO_TRANSMISSION_CAPTURE_ACTIVE_USER_DATA_KEY,
} from './config';
import { renderReadiness } from '../../Rendering/browserReadiness';

interface Props {
    color: string
    depth: number
    floorY: number
    glossiness: number
    positionZ: number
    reflectionRevision?: number
    width: number
}

const REFLECTION_RESOLUTION = 1024;

// Three's Reflector supplies the mirrored camera and projective texture
// matrix. This shader adds a gloss-controlled blur, subtle cyclorama tint, and
// Fresnel weighting before compositing that texture over the physical floor.
const CYCLORAMA_FLOOR_REFLECTION_SHADER = {
    name: 'CycloramaFloorReflection',
    uniforms: {
        color: {value: new Color()},
        glossiness: {value: 0},
        reflectionTexelSize: {
            value: new Vector2(
                1 / REFLECTION_RESOLUTION,
                1 / REFLECTION_RESOLUTION,
            ),
        },
        tDiffuse: {value: null},
        textureMatrix: {value: null},
    },
    vertexShader: `
        uniform mat4 textureMatrix;
        varying vec4 vReflectionUv;
        varying vec3 vViewDirection;
        varying vec3 vWorldNormal;

        #include <common>
        #include <logdepthbuf_pars_vertex>

        void main() {
            vec4 worldPosition = modelMatrix * vec4(position, 1.0);
            vReflectionUv = textureMatrix * vec4(position, 1.0);
            vViewDirection = normalize(cameraPosition - worldPosition.xyz);
            vWorldNormal = normalize(mat3(modelMatrix) * normal);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(
                position,
                1.0
            );

            #include <logdepthbuf_vertex>
        }
    `,
    fragmentShader: `
        uniform vec3 color;
        uniform float glossiness;
        uniform vec2 reflectionTexelSize;
        uniform sampler2D tDiffuse;
        varying vec4 vReflectionUv;
        varying vec3 vViewDirection;
        varying vec3 vWorldNormal;

        #include <logdepthbuf_pars_fragment>

        void main() {
            #include <logdepthbuf_fragment>

            vec2 reflectionUv = (
                vReflectionUv.xy
                / max(vReflectionUv.w, 0.0001)
            );
            float blurPixels = mix(5.0, 0.25, glossiness);
            vec2 blurStep = reflectionTexelSize * blurPixels;
            vec3 reflection = (
                texture2D(tDiffuse, reflectionUv).rgb * 4.0
                + texture2D(
                    tDiffuse,
                    reflectionUv + vec2(blurStep.x, 0.0)
                ).rgb
                + texture2D(
                    tDiffuse,
                    reflectionUv - vec2(blurStep.x, 0.0)
                ).rgb
                + texture2D(
                    tDiffuse,
                    reflectionUv + vec2(0.0, blurStep.y)
                ).rgb
                + texture2D(
                    tDiffuse,
                    reflectionUv - vec2(0.0, blurStep.y)
                ).rgb
                + texture2D(tDiffuse, reflectionUv + blurStep).rgb * 0.5
                + texture2D(tDiffuse, reflectionUv - blurStep).rgb * 0.5
                + texture2D(
                    tDiffuse,
                    reflectionUv + vec2(blurStep.x, -blurStep.y)
                ).rgb * 0.5
                + texture2D(
                    tDiffuse,
                    reflectionUv + vec2(-blurStep.x, blurStep.y)
                ).rgb * 0.5
            ) / 10.0;
            float normalViewDot = clamp(
                dot(normalize(vWorldNormal), normalize(vViewDirection)),
                0.0,
                1.0
            );
            float fresnel = pow(1.0 - normalViewDot, 5.0);
            float reflectionAlpha = glossiness * mix(0.62, 0.92, fresnel);
            vec3 tintedReflection = mix(
                reflection,
                reflection * color,
                0.12
            );

            gl_FragColor = vec4(tintedReflection, reflectionAlpha);
            #include <tonemapping_fragment>
            #include <colorspace_fragment>
        }
    `,
};

/**
 * Captures the scene through a mirrored camera and composites it over the flat
 * cyclorama floor. A managed Three reflector is used instead of Drei's helper
 * so render targets are disposed when gloss is disabled or the scene unmounts.
 */
export default memo(function CycloramaFloorReflection({
    color,
    depth,
    floorY,
    glossiness,
    positionZ,
    reflectionRevision,
    width,
}:Props) {
    const invalidate = useThree(state => state.invalidate);
    const capturedForTransmissionRef = useRef(false);
    const reflectionRevisionRef = useRef(reflectionRevision);
    const reflector = useMemo(() => {
        // Construct manually rather than through JSX so its render callbacks,
        // target lifetime, and nested-pass behavior are fully controlled.
        const nextReflector = new Reflector(
            new PlaneGeometry(1, 1),
            {
                clipBias: 0.0001,
                color: '#ffffff',
                multisample: 0,
                shader: CYCLORAMA_FLOOR_REFLECTION_SHADER,
                textureHeight: REFLECTION_RESOLUTION,
                textureWidth: REFLECTION_RESOLUTION,
            },
        );
        const material = nextReflector.material as ShaderMaterial;
        material.depthWrite = false;
        material.polygonOffset = true;
        material.polygonOffsetFactor = -1;
        material.polygonOffsetUnits = -1;
        material.side = FrontSide;
        material.transparent = true;
        nextReflector.frustumCulled = false;
        nextReflector.renderOrder = 1;
        const renderReflection = nextReflector.onBeforeRender.bind(
            nextReflector,
        );
        nextReflector.onBeforeRender = (...args) => {
            const renderer = args[0];
            const scene = args[1];
            const sceneUserData = scene.userData as Record<string, unknown>;
            const reflectorMaterial = (
                nextReflector.material as ShaderMaterial
            );
            const transmissionCaptureActive = (
                sceneUserData[
                    STUDIO_TRANSMISSION_CAPTURE_ACTIVE_USER_DATA_KEY
                ] === true
            );
            if(
                sceneUserData[SHADOW_BAKE_ACTIVE_USER_DATA_KEY] === true
                || (
                    renderer.getRenderTarget() !== null
                    && !transmissionCaptureActive
                )
            ) {
                // Never leak a prior reflection into shadow or unrelated
                // offscreen targets.
                reflectorMaterial.colorWrite = false;
                return;
            }

            reflectorMaterial.colorWrite = true;
            if(
                !transmissionCaptureActive
                && capturedForTransmissionRef.current
            ) {
                // The transmission pass already refreshed tDiffuse and its
                // texture matrix with this frame's camera. Reuse that exact
                // capture for the main floor instead of rendering it twice.
                return;
            }

            const previousCaptureState = (
                sceneUserData[
                    STUDIO_REFLECTION_CAPTURE_ACTIVE_USER_DATA_KEY
                ]
            );
            const previousTransmissionCaptureState = (
                sceneUserData[
                    STUDIO_TRANSMISSION_CAPTURE_ACTIVE_USER_DATA_KEY
                ]
            );
            if(transmissionCaptureActive) {
                // The nested mirror render is a distinct pass. Mask the outer
                // transmission state so children cannot mistake it for the
                // target that will eventually be sampled by the glass.
                sceneUserData[
                    STUDIO_TRANSMISSION_CAPTURE_ACTIVE_USER_DATA_KEY
                ] = false;
            }
            sceneUserData[
                STUDIO_REFLECTION_CAPTURE_ACTIVE_USER_DATA_KEY
            ] = true;
            try {
                renderReflection(...args);
                capturedForTransmissionRef.current = (
                    transmissionCaptureActive
                );
            } finally {
                sceneUserData[
                    STUDIO_REFLECTION_CAPTURE_ACTIVE_USER_DATA_KEY
                ] = previousCaptureState;
                sceneUserData[
                    STUDIO_TRANSMISSION_CAPTURE_ACTIVE_USER_DATA_KEY
                ] = previousTransmissionCaptureState;
            }
        };

        return nextReflector;
    }, []);

    useLayoutEffect(() => {
        // Acknowledgement occurs in onAfterRender, after the main framebuffer
        // has actually consumed the refreshed reflection target.
        reflectionRevisionRef.current = reflectionRevision;
        if(reflectionRevision !== undefined) {
            renderReadiness.reset('reflection', reflectionRevision);
        }
        invalidate();
    }, [invalidate, reflectionRevision]);

    useLayoutEffect(() => {
        const material = reflector.material as ShaderMaterial;
        (material.uniforms.color.value as Color).set(color);
        material.uniforms.glossiness.value = glossiness;
        invalidate();
    }, [color, glossiness, invalidate, reflector]);

    useLayoutEffect(() => {
        reflector.onAfterRender = (renderer, scene) => {
            (reflector.material as ShaderMaterial).colorWrite = true;
            const sceneUserData = scene.userData as Record<string, unknown>;
            if(
                renderer.getRenderTarget() !== null
                ||
                sceneUserData[SHADOW_BAKE_ACTIVE_USER_DATA_KEY] === true
            ) return;

            capturedForTransmissionRef.current = false;
            if(reflectionRevisionRef.current !== undefined) {
                renderReadiness.acknowledge(
                    'reflection',
                    reflectionRevisionRef.current,
                );
            }
        };

        return () => {
            reflector.onAfterRender = () => undefined;
        };
    }, [reflector]);

    useEffect(() => {
        return () => {
            reflector.geometry.dispose();
            reflector.dispose();
        };
    }, [reflector]);

    return <primitive
        object={reflector}
        position={[0, floorY, positionZ]}
        rotation={[-Math.PI / 2, 0, 0]}
        scale={[width, depth, 1]}
    />;
});
